// Catalog.ID Client — reaching a bitcash wallet key from the key-grant flow
// ============================================================================
// The glue the `bitcash-auth-token` handler injects: how a wallet's SPENDING KEY
// is obtained when a token has to be minted, adopted or revoked. It is the one
// place in this family that fetches and decrypts, and it is kept apart from
// `bitcash-handlers.ts` for exactly the reason the mail family keeps
// `mail-address-store.ts` apart: the gate logic must stay unit-testable, and
// none of this is.
//
// TWO CUSTODY POSTURES, and since 2026-09-17 both of them are PIN-LEVEL:
//
//   account wallet  The list is the account's `cid/bitcash-wallets` document
//                   (cid-encrypted-storage §7.2), encrypted under a key derived
//                   from `derive_root` — which is device-storable, so an
//                   ordinary PIN-unlocked session can read it. NO supersecret
//                   prompt: D5 was relaxed, and with it the old
//                   prompt-per-token-mint. A locked tab reads nothing and the
//                   handler refuses rather than guessing.
//
//   organization    An entitled org key for owner and treasurer (D14), wrapped
//   wallet          to the delegate's own `attr_encrypt` key like every other
//                   org key. Unchanged by the move: an organization has no
//                   derive root its delegates can use (§5.1), so its wallet
//                   stays in the ORG bag.
//
// What went away with the prompt: the member is no longer asked for the
// supersecret every time a module wants a spending token, which was the whole
// point of §7.1. What did NOT go away: the consent dialog. The prompt was never
// the consent — `bitcash-handlers.ts` gate 5 is.

import { keys as keyState } from '../state/keys';
import { identity } from '../state/identity';
import * as delegateApi from '../api/delegate';
import * as aes from '../crypto/aes';
import { unwrapPrivateKey } from '../crypto/claims';
import {
    BITCASH_WALLET_KEY_ID, decodeBitcashWalletEntry,
} from '../crypto/bitcash-wallet-entry';
import { activeWallets, walletSlotsFromDocument, type WalletSlot } from '../crypto/bitcash-wallets-doc';
import { loadWallets } from '../state/bitcash-wallets-store';
import type { BitcashWalletChoice, BitcashOrgWalletOffer } from './bitcash-types';

/** Roles whose delegates hold the organization's wallet key (D14). */
const ORG_WALLET_ROLES = ['owner', 'treasurer'];

/**
 * The account's ACTIVE wallets, from its `cid/bitcash-wallets` document.
 *
 * EMPTY, never a throw: the consent picker and the Connected Modules pane both
 * call this to decide what to OFFER, and a locked tab, an account with no
 * document and a node that would not answer all mean the same thing there —
 * nothing to offer yet. The refusal a member can act on is raised one step
 * later, by {@link getAccountWalletKey} returning null.
 */
export async function listAccountWallets(): Promise<WalletSlot[]> {
    try {
        const loaded = await loadWallets();
        return loaded ? walletSlotsFromDocument(loaded.doc) : [];
    } catch {
        return [];
    }
}

/**
 * The chosen account wallet's spending key, or null when the account holds no
 * ACTIVE wallet in that slot or the document cannot be read (a locked tab, a
 * missing derive root, a node that would not answer).
 *
 * No prompt and no bag read: the document is readable on any PIN-unlocked tab
 * (§7.1). A REVOKED slot answers null deliberately — the member retired that
 * wallet, and minting a spending token on it would be the one thing revoking
 * was meant to prevent.
 */
export async function getAccountWalletKey(
    choice: BitcashWalletChoice,
): Promise<string | null> {
    const slot = choice.keyIndex ?? 0;
    try {
        const loaded = await loadWallets();
        if (!loaded) return null;
        return activeWallets(loaded.doc).find(w => w.slot === slot)?.key ?? null;
    } catch {
        return null;
    }
}

/**
 * The organization wallet offered in the picker, or null when the active
 * identity is not an owner/treasurer delegate or the org has no wallet key
 * wrapped for it.
 */
export async function getOrgWalletOffer(): Promise<BitcashOrgWalletOffer | null> {
    const active = identity.getActive();
    const orgCid = active?.organizationCid;
    if (!active || !orgCid || active.accountType !== 'delegate') return null;
    if (!identity.getRoles().some(r => ORG_WALLET_ROLES.includes(r))) return null;

    try {
        const wrapped = await delegateApi.getWrappedOrgKeys();
        if (!wrapped.some(w => w.keyId === BITCASH_WALLET_KEY_ID)) return null;
    } catch {
        return null;
    }
    // The organization's display name belongs to the org profile; the cid is
    // what this dialog can state truthfully without another round-trip.
    return { orgCid, label: orgCid };
}

/**
 * The organization wallet's spending key through the org key wraps — the
 * delegate's own `attr_encrypt` key unwraps it, so the PIN-unlocked session is
 * enough (D14). Null when it is not available.
 */
export async function getOrgWalletKey(orgCid: string): Promise<string | null> {
    if (identity.getOrganizationCid() !== orgCid) return null;
    const mySecretKey = keyState.getPrivateKey('attr_encrypt');
    if (!mySecretKey) return null;

    try {
        const wrapped = await delegateApi.getWrappedOrgKeys();
        const candidates = wrapped
            .filter(w => w.keyId === BITCASH_WALLET_KEY_ID)
            .sort((a, b) => b.keyIndex - a.keyIndex);
        const head = candidates[0];
        if (!head) return null;
        const privateKeyB64 = await unwrapPrivateKey(head.wrappedKey, aes.fromBase64(mySecretKey));
        return decodeBitcashWalletEntry(privateKeyB64).key;
    } catch {
        return null;
    }
}
